/**
 * Month/Year narrowing for the Renewals list (B1). Independent of the tab's
 * own Expiry Window: Expiry Window controls how FAR the bucket's window
 * reaches (`days` / `lapsed_days`), Month/Year pins the result to one
 * calendar month and/or year of `queue_date` -- the date the renewal desk
 * actually works to (an instalment's next unpaid due date when one is
 * outstanding, else `expiry_date`; see `queue_date_expression`,
 * `backend/apps/policies/lifecycle.py`).
 *
 * Queue #220 removed a Month/Year pair from this page because the backend
 * never read `expiry_month`/`expiry_year` (django-filter drops an unknown
 * param instead of rejecting it, so picking "March" narrowed nothing while
 * looking like a working filter). This time `?month=`/`?year=` are real
 * queue params (`apps/renewals/queue.py::parse_month_year`/
 * `apply_month_year`, proven by `backend/tests/test_b1_renewal_month_year.py`)
 * before the controls come back.
 *
 * The option lists below are scoped to the active tab so a user is never
 * offered a month/year the tab cannot contain: Upcoming only offers a
 * month/year that is not entirely in the past; Lapsed only offers one that is
 * not entirely still ahead. `today` is a parameter (defaulting to `new
 * Date()`) purely so the pure functions are deterministic in tests.
 */
import type { RenewalTab } from './renewalExpiryWindow';
import { EXPIRY_WINDOW_UPCOMING } from './renewalExpiryWindow';

export const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** How many years on either side of today a tab offers. Renewal terms mostly
 *  run about a year, but a board-approved long term is real (a real prod
 *  policy runs 2026-08-06 -> 2036-08-05, see B12) -- 5 years covers the book
 *  the desk actually works without listing decades nobody will ever pick. */
const YEAR_SPAN = 5;

export interface MonthYearOption {
  value: string;
  label: string;
}

/**
 * Year options for one tab, nearest-first: the current year, then
 * progressively further in the tab's own direction. The current year is
 * always included -- it is both "the rest of this year" for Upcoming and
 * "so far this year" for Lapsed.
 */
export function yearOptionsFor(tab: RenewalTab, today: Date = new Date()): MonthYearOption[] {
  const currentYear = today.getFullYear();
  const years =
    tab === EXPIRY_WINDOW_UPCOMING
      ? Array.from({ length: YEAR_SPAN + 1 }, (_, i) => currentYear + i)
      : Array.from({ length: YEAR_SPAN + 1 }, (_, i) => currentYear - i);
  return years.map((y) => ({ value: String(y), label: String(y) }));
}

/**
 * Month options for one tab given the selected year value (`''` for "All
 * years"). A year strictly on the tab's own side of today (a future year for
 * Upcoming, a past year for Lapsed) can contain any month, so all 12 are
 * offered; the CURRENT year can only contain the months on the tab's own
 * side of today, so it is trimmed to those; with no year chosen, every month
 * is offered -- it still narrows correctly once the request reaches the
 * server, since `bucket` alone already confines it to whichever occurrence
 * of that month is upcoming or lapsed.
 */
export function monthOptionsFor(
  tab: RenewalTab,
  yearValue: string,
  today: Date = new Date()
): MonthYearOption[] {
  const allMonths = MONTH_LABELS.map((label, i) => ({ value: String(i + 1), label }));
  if (!yearValue) return allMonths;

  const year = Number(yearValue);
  const currentYear = today.getFullYear();
  if (!Number.isInteger(year) || year !== currentYear) return allMonths;

  const currentMonth = today.getMonth() + 1; // JS months are 0-indexed; the wire value is 1-12.
  return tab === EXPIRY_WINDOW_UPCOMING
    ? allMonths.filter((m) => Number(m.value) >= currentMonth)
    : allMonths.filter((m) => Number(m.value) <= currentMonth);
}
